"use client";

import { useRef } from "react";
import { SectionHead, SectionShell, useBandRowAlign, useWholeRowBlocks } from "@/components/sections/kit";
import { useEnterStagger } from "@/components/sections/kit/motion";
import { SLA_ROWS, SUPPORT_ROWS } from "./data";

const TABLES = [
  { label: "Service level agreement — excerpt", rows: SLA_ROWS },
  { label: "Support model — enterprise", rows: SUPPORT_ROWS },
];

/**
 * SLA & support — the numbers as they appear in the Enterprise agreement.
 * Sits on the kit's translucent `neutral` wash (the grid-paper analogue of
 * the handoff's warm band); below md, where bands don't render, the section
 * paints the warm surface itself.
 */
export default function SlaSupportSection() {
  const rootRef = useRef<HTMLDivElement>(null);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  useEnterStagger(rootRef, "[data-sla-card]", { y: 24, stagger: 0.12 });

  return (
    <SectionShell
      id="sla"
      band="neutral"
      className="scroll-mt-[var(--grid-cell,72px)] max-md:bg-bg-warm-2 md:pt-px md:pb-px"
    >
      <div ref={rootRef}>
        <SectionHead
          eyebrow="In writing"
          title="What the contract actually says."
          lead="Procurement doesn't buy adjectives. These are the numbers as they appear in the Enterprise agreement — same figures as the pricing page."
        />
        <div data-grid-block className="grid items-start gap-4 md:grid-cols-2">
          {TABLES.map((table) => (
            <div
              key={table.label}
              data-sla-card
              className="overflow-hidden rounded-ref-lg border border-line bg-surface shadow-ref"
            >
              <div className="border-b border-line bg-surface-2 px-6 py-[13px] font-jetbrains text-[10.5px] uppercase tracking-[0.12em] text-dim">
                {table.label}
              </div>
              <div className="px-6 pb-5 pt-2">
                {table.rows.map((row) => (
                  <div
                    key={row.k}
                    className="flex items-baseline gap-5 border-b border-line py-[13px] last:border-0 max-md:flex-col max-md:gap-1"
                  >
                    <span className="w-[170px] flex-none text-[13px] font-semibold">{row.k}</span>
                    <span className="text-[13px] leading-[1.55] text-muted">{row.v}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </SectionShell>
  );
}
